// pages/student/Courses.js
import {
  Grid,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  Home,
  School,
  MenuBook,
  TrendingUp,
  EmojiEvents,
  ArrowForward,
} from "@mui/icons-material";
import SideBar from "./components/SideBar";
import Specialization from "./components/Specialization";
import "../../styles/Course.css";

const Courses = () => {
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        setUserInfo(data);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const stats = [
    {
      icon: <MenuBook sx={{ fontSize: 24 }} />,
      label: "Available Courses",
      value: "24",
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.08)",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 24 }} />,
      label: "In Progress",
      value: "4",
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.08)",
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 24 }} />,
      label: "Completed",
      value: "8",
      color: "#A0EF48",
      bgColor: "rgba(160, 239, 72, 0.08)",
    },
    {
      icon: <School sx={{ fontSize: 24 }} />,
      label: "Certificates",
      value: "6",
      color: "#45B7D1",
      bgColor: "rgba(69, 183, 209, 0.08)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {/* Breadcrumbs */}
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <MenuBook sx={{ fontSize: 16 }} />
                  Courses
                </Typography>
              </Breadcrumbs>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Available Courses
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(26, 26, 26, 0.4)",
                  mt: 0.5,
                }}
              >
                Browse all courses and start your learning journey
              </Typography>
            </Grid>

            <Grid item>
              <Chip
                icon={<School sx={{ fontSize: 16 }} />}
                label={`${userInfo?.firstname || "Student"}`}
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  border: "1px solid rgba(43, 173, 101, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderBottom: "1px solid rgba(43, 173, 101, 0.04)",
          }}
        >
          <Grid container spacing={2}>
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    border: "1px solid rgba(43, 173, 101, 0.04)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 20px rgba(43, 173, 101, 0.06)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: stat.bgColor,
                      borderRadius: "50%",
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: stat.color,
                      flexShrink: 0,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: "1.1rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(26, 26, 26, 0.4)",
                        fontSize: "0.7rem",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Specialization Content - All Available Courses */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Specialization />
        </Box>
      </Box>
    </Box>
  );
};

export default Courses;
